import { getLatLng } from '@api/google'
import { PublicLocationWithLanes } from '@clear-denver/tpc-bff-client/build/api/tpc-bff/types'
import { ILaneInfo } from '@components/shared/LaneInfo'
import { calculateSphericalDistance } from '@utils/calculateSphericalDistance'

const METERS_TO_MILES_RATIO = 0.000621371192
const SEARCH_RADIUS_IN_MILES = 100

export interface LocationCoordinates {
  lat: number
  lng: number
}

export type EnrollmentLocation = {
  locationCode: string
  name: string
  address: string
  location: LocationCoordinates
  distance: number
  lanes: ILaneInfo[]
  serviceTerritoryId: string
  conversionCouponInfo?: string
  importantInfo?: string
  openDaily?: boolean
}

export interface NearbyEnrollmentLocations {
  enrollmentLocationsNearSearch: EnrollmentLocation[]
  location: Location
}

export const determineMapZoomLevel = (
  locationsWithinRange: EnrollmentLocation[]
) => {
  const largestDistance = locationsWithinRange.reduce((acc, location) => {
    if (location?.distance > acc) acc = location?.distance
    return acc
  }, 0)

  return largestDistance > 20 ? 8 : largestDistance > 9 ? 10 : undefined
}

export const getNearbyEnrollmentLocations = async (
  enrollmentLocations: PublicLocationWithLanes[],
  searchInput: string
): Promise<NearbyEnrollmentLocations> => {
  const searchCoords = await getLatLng(searchInput, enrollmentLocations)

  const enrollmentLocationsNearSearch: EnrollmentLocation[] = Object.values(
    enrollmentLocations
  )
    .map((enrollmentLocation: any): EnrollmentLocation => {
      const distanceInMeters = calculateSphericalDistance(searchCoords as any, {
        lat: enrollmentLocation.latitude,
        lng: enrollmentLocation.longitude,
      })
      const distanceInMiles = distanceInMeters * METERS_TO_MILES_RATIO
      const roundedDistanceInMiles = Math.round(distanceInMiles * 10) / 10

      return {
        locationCode: enrollmentLocation?.uid,
        distance: roundedDistanceInMiles,
        name: enrollmentLocation?.name,
        location: {
          lat: Number(enrollmentLocation.latitude),
          lng: Number(enrollmentLocation.longitude),
        },
        address: `${enrollmentLocation?.address1} ${
          enrollmentLocation?.address2 || ''
        } ${enrollmentLocation?.city}, ${enrollmentLocation?.state}`,
        lanes: enrollmentLocation?.lanes,
        openDaily: enrollmentLocation?.open,
        conversionCouponInfo: enrollmentLocation?.conversionCouponInfo,
        importantInfo: enrollmentLocation?.importantInfo,
        serviceTerritoryId: enrollmentLocation?.serviceTerritoryId,
      }
    })
    // return enrollment locations within SEARCH_RADIUS_IN_MILES miles
    .filter(
      (enrollmentLocation) =>
        enrollmentLocation.distance < SEARCH_RADIUS_IN_MILES
    )

  return { enrollmentLocationsNearSearch, location }
}
